/*
 * Add Tournament Player — web port of AddTournamentPlayerScreen.
 *
 * Two modes:
 *   1. Add Player      — display name + optional email/phone + handicap.
 *                        If email or phone is provided we route through
 *                        the /players/invite endpoint so the invite
 *                        fires immediately. If both are blank we use
 *                        /players/guest and create a placeholder row
 *                        (status: Not invited). Captain can fill in
 *                        contact later via the edit modal.
 *   2. Find App User   — search existing 18Gimmies usernames, pick one,
 *                        invite by user id. They show as Joined the
 *                        moment they're added because the row carries
 *                        their user_id immediately.
 *
 * Earlier this screen had three modes (Guest, App User, Invite by
 * Contact). Guest and Invite by Contact were the same form with
 * different fields filled in, so the choice was meaningless. The new
 * "Add Player" mode subsumes both — backend routing happens based on
 * whether contact info is present.
 *
 * On success, navigates back to the event detail so the new roster
 * row appears (detail fetch on mount re-pulls).
 */

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Star } from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';

type Mode = 'add' | 'app_user';

export default function AddTournamentPlayer() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const [event, setEvent] = useState(activeEvent?.id === eventId ? activeEvent : null);

  // Owner or captain per the roles spec. Backend allows captains to add
  // players too; bounce only the 'player' role here. Detection is best-
  // effort — when the store is still loading we render normally and
  // re-check after the fetch lands.
  useEffect(() => {
    if (event?.myRole && event.myRole !== 'owner' && event.myRole !== 'captain') {
      navigate(`/app/tournaments/${eventId}/players`, { replace: true });
    }
  }, [event, eventId, navigate]);
  const [mode, setMode] = useState<Mode>('add');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [handicap, setHandicap] = useState('');
  const [teamId, setTeamId] = useState<string>('');
  const [makeCaptain, setMakeCaptain] = useState(false);

  // App-user search. Shape matches the API response from /auth/search:
  // { userId, username, displayName }. An earlier version used `id` here
  // which silently broke the entire app-user invite path (picked.id was
  // undefined, request fell to email/phone branch, server returned 400).
  const [search, setSearch] = useState('');
  type AppUserHit = { userId: string; username: string; displayName: string | null };
  const [results, setResults] = useState<AppUserHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<AppUserHit | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvent = useCallback(async () => {
    if (!eventId) return;
    try {
      const ev = await api.getTournament(eventId);
      setEvent(ev);
      setActiveEvent(ev);
    } catch { /* ignore — empty state will show */ }
  }, [eventId, setActiveEvent]);

  useEffect(() => { if (!event) fetchEvent(); }, [event, fetchEvent]);

  // Debounced username search (300ms)
  useEffect(() => {
    if (mode !== 'app_user' || picked || search.trim().length < 2) {
      setResults([]); return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try { setResults(await api.searchUsers(search.trim())); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [mode, search, picked]);

  const teamOptions = useMemo(() => event?.teams ?? [], [event]);

  // If the user switches to Unassigned after toggling captain on, clear the
  // captain toggle so the state is never "captain of no team."
  useEffect(() => {
    if (!teamId && makeCaptain) setMakeCaptain(false);
  }, [teamId, makeCaptain]);

  // The team object the user is about to add the player to (if any). Used to
  // warn if it already has a captain that would get replaced.
  const selectedTeam = useMemo(
    () => teamOptions.find((t) => t.id === teamId) ?? null,
    [teamOptions, teamId],
  );
  const existingCaptain = useMemo(() => {
    if (!selectedTeam?.captainEventPlayerId || !event) return null;
    return event.players.find((p) => p.id === selectedTeam.captainEventPlayerId) ?? null;
  }, [selectedTeam, event]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!eventId) return;

    // Parse handicap. Accept "+2", "+2.5", ".8" and friends — the same
    // parsing rules PlayerEditModal uses. Plus handicaps are stored as
    // negative numbers; "+2.5" becomes -2.5. Snap to one decimal so we
    // don't write 12.55 into the DB.
    let hcp: number | undefined;
    const rawHcp = handicap.trim();
    if (rawHcp.length > 0) {
      if (!/^[+-]?(\d+(\.\d+)?|\.\d+)$/.test(rawHcp)) {
        setError('Handicap must be a number (e.g. 12, 12.5, +2.5, .8).');
        return;
      }
      const cleaned = rawHcp.replace(/^\+/, '-');
      const n = parseFloat(cleaned);
      if (!Number.isFinite(n) || n < -10 || n > 54) {
        setError('Handicap must be between −10 and 54.');
        return;
      }
      hcp = Math.round(n * 10) / 10;
    }
    const team = teamId || undefined;

    // Roles spec Q5: invite-time role choice is explicit. The existing
    // "Make captain of [team]" checkbox supplies that signal — checked
    // means roleType='captain' (and team is required), unchecked means
    // roleType='player'. The backend wires the captain pointer
    // atomically inside the INSERT, so no separate assignTournamentCaptain
    // call is needed any more.
    const wantRole: 'player' | 'captain' = makeCaptain && team ? 'captain' : 'player';
    if (wantRole === 'captain' && !team) {
      setError('Captains must be assigned to a team. Pick a team first.');
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'app_user') {
        if (!picked) { setError('Pick a user from the search results.'); setSubmitting(false); return; }
        await api.inviteTournamentPlayer(eventId, {
          userId: picked.userId,
          teamId: team,
          eventHandicapIndex: hcp,
          roleType: wantRole,
        });
      } else {
        // 'add' mode. A name is always required. Contact is optional: a
        // contactless entry becomes a guest slot, while an email or phone
        // uses the invite endpoint and sends the invite immediately.
        if (name.trim().length < 1) {
          setError('Display name is required.');
          setSubmitting(false);
          return;
        }
        const trimmedEmail = email.trim();
        const trimmedPhone = phone.trim();
        if (trimmedEmail || trimmedPhone) {
          await api.inviteTournamentPlayer(eventId, {
            displayName: name.trim(),
            email: trimmedEmail || undefined,
            phone: trimmedPhone || undefined,
            teamId: team,
            eventHandicapIndex: hcp,
            roleType: wantRole,
          });
        } else {
          await api.addTournamentGuest(eventId, {
            guestName: name.trim(),
            teamId: team,
            eventHandicapIndex: hcp,
            roleType: wantRole,
          });
        }
      }

      navigate(`/app/tournaments/${eventId}`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to add player');
      setSubmitting(false);
    }
  }

  // Contactless add mode creates a guest slot. Supplying contact still sends
  // an invite immediately, so keep the action label explicit.
  const hasContact = email.trim().length > 0 || phone.trim().length > 0;
  const submitLabel = (() => {
    if (submitting) return mode === 'app_user' ? 'Inviting…' : hasContact ? 'Sending…' : 'Saving…';
    return mode === 'app_user' ? 'Invite User' : hasContact ? 'Send Invite' : 'Add Guest';
  })();

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
      <section className="t-hero">
        <h1>Add Player</h1>
        <p>
          Add a player to the roster now. Email and phone are optional. Add contact details later
          from the player roster when you are ready to send an invite.
        </p>
      </section>

      {error && <div className="t-error">{error}</div>}

      <div className="t-chip-row" style={{ marginBottom: 18 }}>
        {(['add', 'app_user'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            className={`t-chip ${mode === m ? 't-chip-active' : ''}`}
            onClick={() => { setMode(m); setError(null); setPicked(null); setResults([]); }}
          >
            {m === 'add' ? 'Add Player' : 'Find App User'}
          </button>
        ))}
      </div>

      <form className="t-form" onSubmit={handleSubmit}>
        {mode === 'app_user' ? (
          <div className="t-field">
            <label htmlFor="t-search" className="t-label">Search by username</label>
            {picked ? (
              <div className="t-row" style={{ cursor: 'default' }}>
                <div className="t-row-body">
                  <div className="t-row-name">{picked.displayName ?? picked.username}</div>
                  <div className="t-row-sub">@{picked.username}</div>
                </div>
                <button type="button" className="t-btn t-btn-ghost" onClick={() => { setPicked(null); setSearch(''); }}>
                  Clear
                </button>
              </div>
            ) : (
              <>
                <input
                  id="t-search"
                  className="t-input"
                  placeholder="Type a username (min 2 chars)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {searching && <div className="t-hint">Searching…</div>}
                {results.length > 0 && (
                  <div className="t-list" style={{ marginTop: 8 }}>
                    {results.map((u) => (
                      <button
                        type="button"
                        key={u.userId}
                        className="t-row"
                        onClick={() => setPicked(u)}
                      >
                        <div className="t-row-body">
                          <div className="t-row-name">{u.displayName ?? u.username}</div>
                          <div className="t-row-sub">@{u.username}</div>
                        </div>
                        <div className="t-row-chevron"><ChevronRight /></div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="t-field">
              <label htmlFor="t-pname" className="t-label">Display name</label>
              <input
                id="t-pname"
                className="t-input"
                placeholder="e.g. Tom Watson"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
            </div>
            <div className="t-field-row">
              <div className="t-field">
                <label htmlFor="t-pemail" className="t-label">Email (optional)</label>
                <input
                  id="t-pemail"
                  className="t-input"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  inputMode="email"
                />
              </div>
              <div className="t-field">
                <label htmlFor="t-pphone" className="t-label">Phone (optional)</label>
                <input
                  id="t-pphone"
                  className="t-input"
                  placeholder="+1 555-0100"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                />
              </div>
            </div>
            <div className="t-hint" style={{ marginTop: -6, marginBottom: 4 }}>
              Optional. If you provide one now, that contact will get the invite link. Leave both
              blank to add this player as a guest.
            </div>
          </>
        )}

        <div className="t-field-row">
          <div className="t-field">
            <label htmlFor="t-hcp" className="t-label">Handicap index</label>
            <input
              id="t-hcp"
              className="t-input"
              placeholder="e.g. 8.4"
              value={handicap}
              onChange={(e) => setHandicap(e.target.value)}
              inputMode="decimal"
              maxLength={6}
            />
          </div>
          <div className="t-field">
            <div className="t-label">Team</div>
            <div className="t-chip-row">
              <button
                type="button"
                className={`t-chip ${teamId === '' ? 't-chip-active' : ''}`}
                onClick={() => setTeamId('')}
              >
                Unassigned
              </button>
              {teamOptions.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`t-chip ${teamId === t.id ? 't-chip-active' : ''}`}
                  onClick={() => setTeamId(t.id)}
                  style={teamId === t.id && t.primaryColor ? { borderColor: t.primaryColor } : undefined}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {teamId && (
          <div
            className="t-field"
            style={{
              padding: 14,
              borderRadius: 12,
              border: '1px solid var(--border)',
              background: 'var(--card)',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={makeCaptain}
                onChange={(e) => setMakeCaptain(e.target.checked)}
              />
              <Star
                width={16}
                height={16}
                fill={makeCaptain ? 'var(--gold)' : 'none'}
                stroke={makeCaptain ? 'var(--gold)' : 'currentColor'}
              />
              <span style={{ fontWeight: 700 }}>
                Make captain of {selectedTeam?.name ?? 'this team'}
              </span>
            </label>
            {existingCaptain && makeCaptain && (
              <div className="t-hint" style={{ marginTop: 8, color: 'var(--text-sec)' }}>
                {existingCaptain.displayName} is currently the captain. They will be replaced.
              </div>
            )}
            {!existingCaptain && (
              <div className="t-hint" style={{ marginTop: 8, color: 'var(--text-mut)' }}>
                Each team can have one captain. They lead the team and pick draft order.
              </div>
            )}
          </div>
        )}

        <button
          type="submit"
          className="t-btn t-btn-primary t-btn-block"
          disabled={submitting}
        >
          {submitLabel}
        </button>
      </form>
    </AppShell>
  );
}
